import React, { useEffect, useState } from 'react';
import { apiUrl } from '../utils/api';
import { formatCurrency } from '../utils/numberFormatter';
import Autocomplete from '@mui/material/Autocomplete';
import {
  TextField, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  MenuItem, Typography, Snackbar, Alert, Box, Chip, IconButton, Tooltip,
  Paper, Divider, FormControl, InputLabel, Select, CircularProgress, LinearProgress
} from '@mui/material';
import {
  GridColDef, GridRenderCellParams, GridActionsCellItem,
  GridRowModes, GridRowModesModel, GridRowEditStopReasons,
  GridRenderEditCellParams, GridEventListener, GridToolbar
} from '@mui/x-data-grid';
import { DataGrid } from '@mui/x-data-grid';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import SendIcon from '@mui/icons-material/Send';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import Badge from '@mui/material/Badge';
import StyledDataGrid from '../components/StyledDataGrid';
import PODocuments from '../components/PODocuments';

const fieldStyle = { width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #ccc' };
const labelStyle = { fontSize: 12, color: '#555', display: 'block', margin: '4px 0' };

interface POItem {
  item_no: number;
  item_description: string;
  item_quantity: number;
  item_measurement: string;
  buying_price: number;
  currency: string;
  charging_price: number;
  delivery_charge: number;
  category_id: number | null;
  bli_id: number | null;
}

interface PurchaseOrder {
  po_number: string;
  project_id: number;
  mr_serial_no: string | null;
  vendor_name: string;
  date_issued: string;
  delivery_date: string | null;
  po_currency: string;
  status: string;
  items?: POItem[];
}

const QSPurchaseOrders = ({ projectId, access = 'none', userId, projects = [] }) => {
  const [pos, setPOs] = useState<PurchaseOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [poToDelete, setPoToDelete] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'success'
  });

  // PO Header state
  const [header, setHeader] = useState({
    po_number: '',
    project_id: projectId,
    mr_serial_no: null,
    vendor_name: '',
    date_issued: new Date().toISOString().slice(0, 10),
    delivery_date: '',
    delivery_terms: '',
    payment_terms: '',
    po_currency: 'SGD',
    notes: '',
    status: 'Draft',
    issued_by_name: '',
    issued_by_phone: '',
    issued_by_email: '',
    contract_id: null,
    design_change_number: null,
    po_type: null, // PO Type (Contract/VO/Design Change)
    vo_id: null, // VO ID
    is_chargeable: false, // Chargeable/Non-chargeable
    delivery_venue: '',
    delivery_recipient: '',
    delivery_hp: '',
    ordered_by: '',
    approved_by: '',
    contract_from: '',
    contract_to: '',
    warranty_months: '',
    maintenance_service: 'NA',
    created_by: '',
    gst: '',
    terms_and_conditions: '',
    rejection_comments: '',
    mr_for: '',
  });

  // PO Items state
  const [items, setItems] = useState<any[]>([
    {
      _id: 1,
      item_no: 1,
      item_description: '',
      item_quantity: 1,
      item_measurement: 'lot',
      buying_price: 0,
      currency: 'SGD',
      charging_price: 0,
      delivery_charge: 0,
      category_id: null,
      bli_id: null
    }
  ]);

  const [boqLineItems, setBoqLineItems] = useState([]);
  const [budgetCategories, setBudgetCategories] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isViewMode, setIsViewMode] = useState(false);
  const [poTotalsByNumber, setPoTotalsByNumber] = useState({});
  const [poForOptions, setPoForOptions] = useState([]); // Options for Contract/VO/DC
  const [poForLoading, setPoForLoading] = useState(false); // Loading state
  const [allPONumbers, setAllPONumbers] = useState([]); // All PO numbers for validation
  const [selectedPONumber, setSelectedPONumber] = useState(null); // Currently editing PO number

  // Company data state for displaying logo and address on PO
  const [companyData, setCompanyData] = useState({
    company_id: null,
    company_name: '',
    logo_url: '',
    address: '',
    phone: '',
    email: '',
    website: '',
    standard_terms: '',
    number_format: '1,234.56' // Default format
  });
  const [companyDataLoading, setCompanyDataLoading] = useState(false);
  const [documentsDialogOpen, setDocumentsDialogOpen] = useState(false);
  const [selectedPOForDocs, setSelectedPOForDocs] = useState<any>(null);
  const [itemRowModesModel, setItemRowModesModel] = useState<GridRowModesModel>({});

  // Fetch POs for the project (exclude Pending Approval - those show in MR/PO Approval page)
  const fetchPOs = async () => {
    if (!projectId) {
      setPOs([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(apiUrl(`/api/purchase-orders?project_id=${projectId}`), {
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to fetch POs');

      const data = await response.json();
      // Show all POs including Rejected (so QS can see and resubmit)
      const filteredData = Array.isArray(data) ? data : [];
      setPOs(filteredData);

      // Extract all PO numbers for validation
      setAllPONumbers(data.map(po => po.po_number));

      // Calculate totals for each PO
      const entries = await Promise.all(
        filteredData.map(async (po) => {
          try {
            const res = await fetch(apiUrl(`/api/purchase-orders/${encodeURIComponent(po.po_number)}/items`), {
              credentials: 'include'
            });
            const items = res.ok ? await res.json() : [];
            const toNum = v => (v === '' || v === null || v === undefined) ? 0 : +v;
            // Grand Total = sum(quantity * buying_price + delivery_charge)
            const grandTotal = items.reduce((s, it) => {
              return s + (toNum(it.item_quantity) * toNum(it.buying_price)) + toNum(it.delivery_charge);
            }, 0);
            return [po.po_number, grandTotal];
          } catch {
            return [po.po_number, 0];
          }
        })
      );
      const map = {};
      entries.forEach(([k, v]) => { map[k] = v; });
      setPoTotalsByNumber(map);
    } catch (error) {
      console.error('Error fetching POs:', error);
      setSnackbar({
        open: true,
        message: 'Failed to fetch Purchase Orders',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch BOQ line items and enrich with prices from rate sheet (same as MR)
  const fetchBoqLineItems = async () => {
    if (!projectId) {
      setBoqLineItems([]);
      return;
    }

    try {
      const response = await fetch(apiUrl(`/api/budget-line-items?project_id=${projectId}`), {
        credentials: 'include'
      });
      if (response.ok) {
        const budgetItems = await response.json();
        const items = Array.isArray(budgetItems) ? budgetItems : [];

        // Fetch rate sheets to get prices
        try {
          const rateSheetRes = await fetch(apiUrl(`/api/rate-sheets?project_id=${projectId}`), {
            credentials: 'include'
          });
          if (rateSheetRes.ok) {
            const rateSheets = await rateSheetRes.json();
            const activeSheet = Array.isArray(rateSheets)
              ? rateSheets.find(rs => rs.is_active) || rateSheets[0]
              : null;

            if (activeSheet) {
              const rateItemsRes = await fetch(apiUrl(`/api/rate-sheets/${activeSheet.rate_sheet_id}/items`), {
                credentials: 'include'
              });
              if (rateItemsRes.ok) {
                const rateItems = await rateItemsRes.json();
                const rateItemsArray = Array.isArray(rateItems) ? rateItems : [];

                const enrichedItems = items.map(item => {
                  const rateItem = rateItemsArray.find(ri => ri.bli_id === item.bli_id);
                  return {
                    ...item,
                    unit_price: rateItem ? rateItem.unit_price : 0
                  };
                });

                setBoqLineItems(enrichedItems);
                return;
              }
            }
          }
        } catch (err) {
          console.error('Error fetching rate sheet items:', err);
        }

        setBoqLineItems(items.map(item => ({ ...item, unit_price: 0 })));
      }
    } catch (error) {
      console.error('Error fetching BOQ line items:', error);
    }
  };

  // Fetch Budget Categories for project
  const fetchBudgetCategories = async () => {
    if (!projectId) {
      setBudgetCategories([]);
      return;
    }
    try {
      const response = await fetch(apiUrl(`/api/budget-categories?project_id=${projectId}`), {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setBudgetCategories(data || []);
      }
    } catch (error) {
      console.error('Error fetching budget categories:', error);
      setBudgetCategories([]);
    }
  };

  // NEW: Fetch PO For options based on PO Type
  const fetchPoForOptions = async (poType: string, projId: number) => {
    try {
      setPoForLoading(true);
      let endpoint = '';
      if (poType === 'Contract') {
        endpoint = `/api/material-requisitions/options/contracts/${projId}`;
      } else if (poType === 'VO') {
        endpoint = `/api/material-requisitions/options/vos/${projId}`;
      } else if (poType === 'Design Change') {
        endpoint = `/api/material-requisitions/options/design-changes/${projId}`;
      }

      if (!endpoint) {
        setPoForOptions([]);
        setPoForLoading(false);
        return;
      }

      const res = await fetch(apiUrl(endpoint), {
        credentials: 'include'
      });
      if (!res.ok) {
        console.error('Failed to fetch PO For options');
        setPoForOptions([]);
        setPoForLoading(false);
        return;
      }

      const data = await res.json();

      // Transform data to common format {id, label}
      let options = [];
      if (poType === 'Contract' && data.contracts) {
        options = data.contracts.map((c: any) => ({
          id: c.contract_id,
          label: `${c.contract_number} - ${c.title || 'Untitled'}`
        }));
      } else if (poType === 'VO' && data.vos) {
        options = data.vos.map((vo: any) => ({
          id: vo.vo_id,
          label: `${vo.contract_number} - ${vo.title || 'Untitled'}`
        }));
      } else if (poType === 'Design Change' && data.design_changes) {
        options = data.design_changes.map((dc: any) => ({
          id: dc.change_number,
          label: `${dc.change_number} - ${dc.change_name || 'Untitled'}`
        }));
      }

      setPoForOptions(options);
      setPoForLoading(false);
    } catch (error) {
      console.error('Error fetching PO For options:', error);
      setPoForOptions([]);
      setPoForLoading(false);
    }
  };

  // Auto-generate PO number
  const autoGeneratePONumber = () => {
    const prefix = (header.po_number?.slice(0, 1) || 'P').toUpperCase();
    let max = 0;
    (allPONumbers || []).forEach(poNum => {
      if (poNum && typeof poNum === 'string' && poNum.startsWith(prefix)) {
        const n = parseInt(poNum.slice(1), 10);
        if (!isNaN(n)) max = Math.max(max, n);
      }
    });
    const next = `${prefix}${String(max + 1).padStart(5, '0')}`;
    setHeader({ ...header, po_number: next });
  };

  // Fetch company data for PO form (logo, address, etc.)
  const fetchCompanyData = async () => {
    if (!projectId || !projects || projects.length === 0) {
      setCompanyData({
        company_id: null,
        company_name: '',
        logo_url: '',
        address: '',
        phone: '',
        email: '',
        website: '',
        standard_terms: '',
        number_format: '1,234.56'
      });
      setCompanyDataLoading(false);
      return;
    }

    try {
      setCompanyDataLoading(true);

      // Find current project from projects array
      const currentProject = projects.find(p => p.project_id === projectId);
      if (!currentProject || !currentProject.company_id) {
        setCompanyDataLoading(false);
        return;
      }

      // Fetch company details
      const response = await fetch(apiUrl(`/api/companies/${currentProject.company_id}`), {
        credentials: 'include'
      });

      if (!response.ok) {
        console.error('Failed to fetch company data');
        setCompanyDataLoading(false);
        return;
      }

      const data = await response.json();
      setCompanyData({
        company_id: data.company_id,
        company_name: data.company_name || '',
        logo_url: data.logo_url || '',
        address: data.address || '',
        phone: data.phone || '',
        email: data.email || '',
        website: data.website || '',
        standard_terms: data.standard_terms || '',
        number_format: data.number_format || '1,234.56'
      });
      setCompanyDataLoading(false);

      // Auto-fill T&C for new PO (not when editing)
      if (!isEditMode && data.standard_terms) {
        setHeader(prev => ({
          ...prev,
          terms_and_conditions: data.standard_terms
        }));
      }
    } catch (error) {
      console.error('Error fetching company data:', error);
      setCompanyDataLoading(false);
    }
  };

  // PO number validation
  const poNumberValidity = React.useMemo(() => {
    const poNum = (header.po_number || '').trim();
    if (!poNum) return { ok: false, msg: 'Required' };
    if (!/^[A-Za-z][0-9]{5}$/.test(poNum)) return { ok: false, msg: 'Invalid format' };
    if (allPONumbers.includes(poNum) && poNum !== selectedPONumber) return { ok: false, msg: 'Already exists' };
    return { ok: true, msg: 'Valid' };
  }, [header.po_number, allPONumbers, selectedPONumber]);

  useEffect(() => {
    fetchPOs();
    fetchBoqLineItems();
    fetchBudgetCategories();
    fetchCompanyData(); // Fetch company data on mount
  }, [projectId, projects]);

  // Load PO for editing
  const loadPO = async (po_number: string) => {
    try {
      const response = await fetch(apiUrl(`/api/purchase-orders/${po_number}`), {
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to fetch PO details');

      const data = await response.json();

      // Block editing of Approved POs — show preview instead
      if (data.status === 'Approved') {
        setSnackbar({
          open: true,
          message: 'This PO is already approved and cannot be edited.',
          severity: 'warning'
        });
        handleViewPO(po_number);
        return;
      }

      // Determine po_type based on which field is set
      let poType = null;
      if (data.contract_id) poType = 'Contract';
      else if (data.vo_id) poType = 'VO';
      else if (data.design_change_number) poType = 'Design Change';

      setHeader({
        po_number: data.po_number,
        project_id: data.project_id,
        mr_serial_no: data.mr_serial_no,
        vendor_name: data.vendor_name || '',
        date_issued: data.date_issued || new Date().toISOString().slice(0, 10),
        delivery_date: data.delivery_date || '',
        delivery_terms: data.delivery_terms || '',
        payment_terms: data.payment_terms || '',
        po_currency: data.po_currency || 'SGD',
        notes: data.notes || '',
        terms_and_conditions: data.terms_and_conditions || '',
        status: data.status,
        issued_by_name: data.issued_by_name || '',
        issued_by_phone: data.issued_by_phone || '',
        issued_by_email: data.issued_by_email || '',
        contract_id: data.contract_id,
        design_change_number: data.design_change_number,
        po_type: poType,
        vo_id: data.vo_id,
        is_chargeable: data.is_chargeable || false,
        delivery_venue: data.delivery_venue || '',
        delivery_recipient: data.delivery_recipient || '',
        delivery_hp: data.delivery_hp || '',
        ordered_by: data.ordered_by || '',
        approved_by: data.approved_by || '',
        contract_from: data.contract_from || '',
        contract_to: data.contract_to || '',
        warranty_months: data.warranty_months || '',
        maintenance_service: data.maintenance_service || 'NA',
        created_by: data.created_by || '',
        gst: data.gst || '',
        rejection_comments: data.rejection_comments || '',
        mr_for: data.mr_for || '',
      });

      // Fetch options if po_type exists
      if (poType && data.project_id) {
        fetchPoForOptions(poType, data.project_id);
      }

      const loadedItems = data.items && data.items.length > 0
        ? data.items.map((it: any) => ({ ...it, _id: it.item_no }))
        : [{ _id: 1, item_no: 1, item_description: '', item_quantity: 1, item_measurement: 'lot', buying_price: 0, currency: 'SGD', charging_price: 0, delivery_charge: 0, category_id: null, bli_id: null }];
      setItems(loadedItems);
      setItemRowModesModel({});

      setSelectedPONumber(po_number);
      setIsEditMode(true);
      setIsViewMode(false);
      setDialogOpen(true);
    } catch (error) {
      console.error('Error loading PO:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load PO details',
        severity: 'error'
      });
    }
  };

  // View PO (read-only)
  const handleViewPO = async (po_number: string) => {
    try {
      const response = await fetch(apiUrl(`/api/purchase-orders/${po_number}`), {
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to fetch PO details');

      const data = await response.json();

      // Determine po_type based on which field is set
      let poType = null;
      if (data.contract_id) poType = 'Contract';
      else if (data.vo_id) poType = 'VO';
      else if (data.design_change_number) poType = 'Design Change';

      setHeader({
        po_number: data.po_number,
        project_id: data.project_id,
        mr_serial_no: data.mr_serial_no,
        vendor_name: data.vendor_name || '',
        date_issued: data.date_issued || new Date().toISOString().slice(0, 10),
        delivery_date: data.delivery_date || '',
        delivery_terms: data.delivery_terms || '',
        payment_terms: data.payment_terms || '',
        po_currency: data.po_currency || 'SGD',
        notes: data.notes || '',
        terms_and_conditions: data.terms_and_conditions || '',
        status: data.status,
        issued_by_name: data.issued_by_name || '',
        issued_by_phone: data.issued_by_phone || '',
        issued_by_email: data.issued_by_email || '',
        contract_id: data.contract_id,
        design_change_number: data.design_change_number,
        po_type: poType,
        vo_id: data.vo_id,
        is_chargeable: data.is_chargeable || false,
        delivery_venue: data.delivery_venue || '',
        delivery_recipient: data.delivery_recipient || '',
        delivery_hp: data.delivery_hp || '',
        ordered_by: data.ordered_by || '',
        approved_by: data.approved_by || '',
        contract_from: data.contract_from || '',
        contract_to: data.contract_to || '',
        warranty_months: data.warranty_months || '',
        maintenance_service: data.maintenance_service || 'NA',
        created_by: data.created_by || '',
        gst: data.gst || '',
        rejection_comments: data.rejection_comments || '',
        mr_for: data.mr_for || '',
      });

      // Fetch options if po_type exists (for display only)
      if (poType && data.project_id) {
        fetchPoForOptions(poType, data.project_id);
      }

      const viewItems = data.items && data.items.length > 0
        ? data.items.map((it: any) => ({ ...it, _id: it.item_no }))
        : [{ _id: 1, item_no: 1, item_description: '', item_quantity: 1, item_measurement: 'lot', buying_price: 0, currency: 'SGD', charging_price: 0, delivery_charge: 0, category_id: null, bli_id: null }];
      setItems(viewItems);
      setItemRowModesModel({});

      setSelectedPONumber(po_number);
      setIsEditMode(false);
      setIsViewMode(true);
      setDialogOpen(true);
    } catch (error) {
      console.error('Error loading PO:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load PO details',
        severity: 'error'
      });
    }
  };

  // Handle save PO
  const handleSavePO = async () => {
    try {
      setSaving(true);

      let response;
      if (isEditMode) {
        // Update existing PO - use selectedPONumber (original) in URL, new po_number goes in body
        response = await fetch(apiUrl(`/api/purchase-orders/${selectedPONumber}`), {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify(header)
        });

        if (response.ok) {
          // After update, use the new po_number for item operations
          const updatedPoNumber = header.po_number;
          // Update/Create items separately - strip _id before sending
          for (const item of items) {
            const { _id, rowNum, line_total, ...itemPayload } = item;
            let itemResponse;
            if (item.item_no) {
              // Update existing item
              itemResponse = await fetch(apiUrl(`/api/purchase-orders/${updatedPoNumber}/items/${item.item_no}`), {
                method: 'PUT',
                headers: {
                  'Content-Type': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify(itemPayload)
              });
            } else {
              // Create new item (added during edit)
              itemResponse = await fetch(apiUrl(`/api/purchase-orders/${updatedPoNumber}/items`), {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify(itemPayload)
              });
            }

            if (!itemResponse.ok) {
              const error = await itemResponse.json();
              console.error('Item save error:', error);
              throw new Error(error.error || 'Failed to save item');
            }
          }
        }
      } else {
        // Create new direct PO (without MR) - strip _id before sending
        const cleanItems = items.map(({ _id, rowNum, line_total, ...rest }) => rest);
        response = await fetch(apiUrl('/api/purchase-orders'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify({
            ...header,
            items: cleanItems
          })
        });
      }

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to save PO');
      }

      setSnackbar({
        open: true,
        message: isEditMode ? 'PO updated successfully' : 'PO created successfully',
        severity: 'success'
      });

      handleCloseDialog();
      fetchPOs();
      window.dispatchEvent(new CustomEvent('refresh-pending-counts'));
    } catch (error) {
      console.error('Error saving PO:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Failed to save PO',
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  // Handle send for approval
  const handleSendForApproval = async (po_number: string) => {
    try {
      const response = await fetch(apiUrl(`/api/purchase-orders/${po_number}/status`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ status: 'Pending Approval' })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to send PO for approval');
      }

      setSnackbar({
        open: true,
        message: 'PO sent for approval successfully',
        severity: 'success'
      });

      fetchPOs();
      window.dispatchEvent(new CustomEvent('refresh-pending-counts'));
    } catch (error) {
      console.error('Error sending PO for approval:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Failed to send PO for approval',
        severity: 'error'
      });
    }
  };

  // Handle delete PO
  const handleDeletePO = async () => {
    if (!poToDelete) return;

    try {
      const response = await fetch(apiUrl(`/api/purchase-orders/${poToDelete}`), {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to delete PO');
      }

      setSnackbar({
        open: true,
        message: 'PO deleted successfully',
        severity: 'success'
      });

      setDeleteDialogOpen(false);
      setPoToDelete(null);
      fetchPOs();
      window.dispatchEvent(new CustomEvent('refresh-pending-counts'));
    } catch (error) {
      console.error('Error deleting PO:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Failed to delete PO',
        severity: 'error'
      });
    }
  };

  // Handle add new PO
  const handleAddNewPO = () => {
    setHeader({
      po_number: '',
      project_id: projectId,
      mr_serial_no: null,
      vendor_name: '',
      date_issued: new Date().toISOString().slice(0, 10),
      delivery_date: '',
      delivery_terms: '',
      payment_terms: '',
      po_currency: 'SGD',
      notes: '',
      terms_and_conditions: companyData.standard_terms || '', // Use company standard terms
      status: 'Draft',
      issued_by_name: '',
      issued_by_phone: '',
      issued_by_email: '',
      contract_id: null,
      design_change_number: null,
      po_type: null,
      vo_id: null,
      is_chargeable: false,
      delivery_venue: '',
      delivery_recipient: '',
      delivery_hp: '',
      ordered_by: '',
      approved_by: '',
      contract_from: '',
      contract_to: '',
      warranty_months: '',
      maintenance_service: 'NA',
      created_by: '',
      gst: '',
      rejection_comments: '',
      mr_for: '',
    });
    setItems([{
      _id: `temp_${Date.now()}`,
      item_no: null,
      item_description: '',
      item_quantity: 1,
      item_measurement: 'lot',
      buying_price: 0,
      currency: 'SGD',
      charging_price: 0,
      delivery_charge: 0,
      category_id: null,
      bli_id: null
    }]);
    setItemRowModesModel({});
    setPoForOptions([]); // Clear PO For options
    setSelectedPONumber(null);
    setIsEditMode(false);
    setIsViewMode(false);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setIsEditMode(false);
    setIsViewMode(false);
    setPoForOptions([]); // Clear options on close
  };

  // DataGrid item handlers
  const handleAddItem = () => {
    const tempId = `temp_${Date.now()}`;
    const newItem = {
      _id: tempId,
      item_no: null,
      item_description: '',
      item_quantity: 1,
      item_measurement: 'lot',
      buying_price: 0,
      currency: header.po_currency || 'SGD',
      charging_price: 0,
      delivery_charge: 0,
      category_id: null,
      bli_id: null
    };
    setItems(prev => [...prev, newItem]);
    setItemRowModesModel(prev => ({
      ...prev,
      [tempId]: { mode: GridRowModes.Edit, fieldToFocus: 'item_description' }
    }));
  };

  const handleItemEditClick = (id: any) => () => {
    setItemRowModesModel(prev => ({ ...prev, [id]: { mode: GridRowModes.Edit } }));
  };

  const handleItemSaveClick = (id: any) => () => {
    setItemRowModesModel(prev => ({ ...prev, [id]: { mode: GridRowModes.View } }));
  };

  const handleItemCancelClick = (id: any) => () => {
    setItemRowModesModel(prev => ({
      ...prev,
      [id]: { mode: GridRowModes.View, ignoreModifications: true }
    }));
    // Remove temp rows that were never saved
    if (String(id).startsWith('temp_')) {
      setItems(prev => prev.filter(item => (item as any)._id !== id));
    }
  };

  const handleItemDeleteClick = (id: any) => () => {
    setItems(prev => prev.filter(item => ((item as any)._id || item.item_no) !== id));
  };

  const processItemRowUpdate = (newRow: any) => {
    setItems(prev => prev.map(item => {
      const rowId = (item as any)._id || item.item_no;
      if (rowId === newRow._id || rowId === newRow.item_no) {
        return { ...item, ...newRow };
      }
      return item;
    }));
    return newRow;
  };

  const handleItemRowModesModelChange = (newModel: GridRowModesModel) => {
    setItemRowModesModel(newModel);
  };

  const handleItemRowEditStop: GridEventListener<'rowEditStop'> = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  // Description Autocomplete edit cell for DataGrid
  const DescriptionEditCell = (params: GridRenderEditCellParams) => {
    return (
      <Autocomplete
        freeSolo
        fullWidth
        size="small"
        options={boqLineItems}
        getOptionLabel={(option: any) => {
          if (typeof option === 'string') return option;
          return option.item_description || '';
        }}
        value={params.value || ''}
        onChange={(event, newValue) => {
          if (typeof newValue === 'string') {
            params.api.setEditCellValue({ id: params.id, field: 'item_description', value: newValue });
          } else if (newValue) {
            params.api.setEditCellValue({ id: params.id, field: 'item_description', value: newValue.item_description });
            setTimeout(() => {
              params.api.setEditCellValue({ id: params.id, field: 'buying_price', value: newValue.unit_price || 0 });
              params.api.setEditCellValue({ id: params.id, field: 'item_measurement', value: newValue.unit_code || 'lot' });
              params.api.setEditCellValue({ id: params.id, field: 'category_id', value: newValue.category_id || null });
              params.api.setEditCellValue({ id: params.id, field: 'bli_id', value: newValue.bli_id || null });
            }, 100);
          }
        }}
        onInputChange={(event, newInputValue, reason) => {
          if (reason === 'input') {
            params.api.setEditCellValue({ id: params.id, field: 'item_description', value: newInputValue });
          }
        }}
        renderOption={(props, option: any) => {
          const { key, ...otherProps } = props;
          return (
            <Box component="li" key={key} {...otherProps} sx={{ display: 'block', py: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {option.item_description || option.item_code}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {option.category_name} • {option.unit_code} • ${option.unit_price || 0}
              </Typography>
            </Box>
          );
        }}
        filterOptions={(options, { inputValue }) => {
          return options.filter((option: any) =>
            option.item_description?.toLowerCase().includes(inputValue.toLowerCase()) ||
            option.category_name?.toLowerCase().includes(inputValue.toLowerCase())
          );
        }}
        renderInput={(inputParams) => (
          <TextField
            {...inputParams}
            variant="standard"
            placeholder="Select or type"
            sx={{ '& .MuiInput-root': { fontSize: '0.875rem' } }}
          />
        )}
        sx={{ width: '100%', '& .MuiAutocomplete-input': { minWidth: '100px !important' } }}
      />
    );
  };

  // Check if any item has PM comment
  const hasPmComments = items.some(item => (item as any).pm_comment);

  // PO Items DataGrid columns
  const poItemColumns: GridColDef[] = [
    {
      field: 'rowNum',
      headerName: '#',
      width: 40,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const index = items.findIndex(item => ((item as any)._id || item.item_no) === params.id);
        return index + 1;
      }
    },
    {
      field: 'item_description',
      headerName: 'Description',
      flex: 2,
      minWidth: 180,
      editable: !isViewMode,
      renderEditCell: (params) => <DescriptionEditCell {...params} />
    },
    {
      field: 'category_id',
      headerName: 'Category',
      width: 120,
      editable: !isViewMode,
      type: 'singleSelect',
      valueOptions: budgetCategories.map((cat: any) => ({ value: cat.category_id, label: cat.name })),
      renderCell: (params) => {
        const cat = budgetCategories.find((c: any) => c.category_id === params.value);
        return cat ? cat.name : '';
      }
    },
    {
      field: 'item_quantity',
      headerName: 'Qty',
      width: 60,
      type: 'number',
      editable: !isViewMode,
      align: 'right',
      headerAlign: 'right'
    },
    {
      field: 'item_measurement',
      headerName: 'UOM',
      width: 60,
      editable: !isViewMode
    },
    {
      field: 'buying_price',
      headerName: 'Buy Price',
      width: 100,
      type: 'number',
      editable: !isViewMode,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => formatCurrency(params.value || 0, header.po_currency, companyData.number_format)
    },
    {
      field: 'currency',
      headerName: 'Curr',
      width: 60,
      editable: !isViewMode
    },
    {
      field: 'charging_price',
      headerName: 'Chrg Price',
      width: 100,
      type: 'number',
      editable: !isViewMode,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => formatCurrency(params.value || 0, header.po_currency, companyData.number_format)
    },
    {
      field: 'delivery_charge',
      headerName: 'Del Chrg',
      width: 90,
      type: 'number',
      editable: !isViewMode,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => formatCurrency(params.value || 0, header.po_currency, companyData.number_format)
    },
    {
      field: 'line_total',
      headerName: 'Total',
      width: 100,
      sortable: true,
      filterable: false,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => {
        const qty = parseFloat(params.row.item_quantity) || 0;
        const price = parseFloat(params.row.buying_price) || 0;
        const delivery = parseFloat(params.row.delivery_charge) || 0;
        return formatCurrency(qty * price + delivery, header.po_currency, companyData.number_format);
      }
    },
    ...(hasPmComments ? [{
      field: 'pm_comment',
      headerName: 'PM Comment',
      flex: 1,
      minWidth: 100,
      renderCell: (params: GridRenderCellParams) => params.value || ''
    }] : []),
    ...(!isViewMode ? [{
      field: 'actions',
      type: 'actions' as const,
      headerName: '',
      width: 100,
      cellClassName: 'actions',
      getActions: ({ id }: { id: any }) => {
        const isInEditMode = itemRowModesModel[id]?.mode === GridRowModes.Edit;

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              icon={<SaveIcon />}
              label="Save"
              color="primary"
              onClick={handleItemSaveClick(id)}
            />,
            <GridActionsCellItem
              icon={<CancelIcon />}
              label="Cancel"
              className="textPrimary"
              onClick={handleItemCancelClick(id)}
              color="inherit"
            />
          ];
        }

        return [
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Edit"
            className="textPrimary"
            onClick={handleItemEditClick(id)}
            color="inherit"
          />,
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label="Delete"
            onClick={handleItemDeleteClick(id)}
            color="inherit"
          />
        ];
      }
    }] : []),
  ];

  // Prepare items rows with unique IDs
  const itemRows = items.map((item, index) => ({
    ...item,
    _id: (item as any)._id || item.item_no || `row_${index}`
  }));

  // Calculate grand total
  const calculateGrandTotal = () => {
    return items.reduce((sum, item) => {
      const itemTotal = (parseFloat(String(item.buying_price)) || 0) * (parseFloat(String(item.item_quantity)) || 0) +
        (parseFloat(String(item.delivery_charge)) || 0);
      return sum + itemTotal;
    }, 0);
  };

  // DataGrid columns
  const columns: GridColDef[] = [
    {
      field: 'po_number',
      headerName: 'PO Number',
      flex: 1,
      minWidth: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Typography
          variant="body2"
          fontWeight={600}
          sx={{
            color: '#1976d2',
            cursor: 'pointer',
            '&:hover': { textDecoration: 'underline' }
          }}
          onClick={() => {
            if (params.row.status === 'Approved') {
              handleViewPO(params.value);
            } else {
              loadPO(params.value);
            }
          }}
        >
          {params.value}
        </Typography>
      )
    },
    {
      field: 'mr_serial_no',
      headerName: 'MR Number',
      flex: 1,
      minWidth: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2">{params.value || 'NA'}</Typography>
      )
    },
    {
      field: 'po_for',
      headerName: 'Reference',
      flex: 1.5,
      minWidth: 180,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" noWrap title={params.value}>
          {params.value || '-'}
        </Typography>
      )
    },
    {
      field: 'vendor_name',
      headerName: 'Vendor',
      flex: 1.5,
      minWidth: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2">{params.value || '-'}</Typography>
      )
    },
    {
      field: 'date_issued',
      headerName: 'Date Issued',
      flex: 1,
      minWidth: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2">{params.value || '-'}</Typography>
      )
    },
    {
      field: 'delivery_date',
      headerName: 'Delivery Date',
      flex: 1,
      minWidth: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2">{params.value || '-'}</Typography>
      )
    },
    {
      field: 'grand_total',
      headerName: 'Grand Total',
      flex: 1,
      minWidth: 130,
      align: 'right',
      headerAlign: 'right',
      valueGetter: (value, row) => {
        const total = poTotalsByNumber[row.po_number] ?? 0;
        return formatCurrency(total, row.po_currency || 'SGD', companyData.number_format);
      },
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" fontWeight={600}>
          {params.value}
        </Typography>
      )
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      minWidth: 110,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value || 'Draft'}
          size="small"
          color={
            params.value === 'Approved' ? 'success' :
            params.value === 'Rejected' ? 'error' :
            params.value === 'Pending Approval' ? 'warning' :
            'default'
          }
          onClick={(e) => e.stopPropagation()}
          sx={{ fontWeight: 600 }}
        />
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 1.5,
      minWidth: 150,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams) => {
        const isDraft = params.row.status === 'Draft' || params.row.status === 'Rejected';

        return (
          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
            {/* View button - always visible */}
            <Tooltip title="View Details">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleViewPO(params.row.po_number);
                }}
                color="primary"
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            {/* Documents button - always visible */}
            <Tooltip title="Documents">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedPOForDocs(params.row);
                  setDocumentsDialogOpen(true);
                }}
                color="default"
              >
                <Badge badgeContent={params.row.document_count || 0} color={params.row.document_count > 0 ? 'primary' : 'default'} max={99}>
                  <AttachFileIcon fontSize="small" />
                </Badge>
              </IconButton>
            </Tooltip>

            {/* Edit button - only for Draft POs */}
            {isDraft && (
              <Tooltip title="Edit">
                <span>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      loadPO(params.row.po_number);
                    }}
                    disabled={access !== 'write'}
                    color="primary"
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}

            {/* Send for Approval button - only for Draft POs */}
            {isDraft && (
              <Tooltip title="Send for Approval">
                <span>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSendForApproval(params.row.po_number);
                    }}
                    disabled={access !== 'write'}
                    color="success"
                  >
                    <SendIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}

            {/* Delete button - always show for all POs */}
            <Tooltip title="Delete">
              <span>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setPoToDelete(params.row.po_number);
                    setDeleteDialogOpen(true);
                  }}
                  disabled={access !== 'write'}
                  color="error"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        );
      }
    }
  ];

  return (
    <Box sx={{ padding: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: 2 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddNewPO}
          disabled={access !== 'write' || !projectId}
        >
          Create PO
        </Button>
      </Box>


      <Paper sx={{ width: '100%' }}>
        {loading && <LinearProgress />}
        <StyledDataGrid
          rows={pos}
          columns={columns}
          loading={loading}
          getRowId={(row) => row.po_number}
          pageSizeOptions={[10, 25, 50, 100]}
          onRowDoubleClick={(params) => handleViewPO(params.row.po_number)}
          sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' } }}
        />
      </Paper>

      {/* PO Edit/Create/View Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="lg" fullWidth>
        <DialogTitle>
          {isViewMode ? `View PO: ${header.po_number}` : (isEditMode ? `Edit PO: ${header.po_number}` : 'Create Purchase Order')}
        </DialogTitle>
        <DialogContent dividers>
          <div className={isViewMode ? 'doc-preview' : undefined}>
          {/* Manager Rejection Comments */}
          {header.rejection_comments && (
            <Box sx={{
              p: 2.5,
              mb: 3,
              bgcolor: '#fff5f5',
              border: '2px solid #f44336',
              borderLeft: '6px solid #f44336',
              borderRadius: 2,
              display: 'flex',
              gap: 1.5,
              alignItems: 'flex-start'
            }}>
              <Box sx={{ color: '#f44336', mt: 0.2, fontSize: 20, fontWeight: 'bold', lineHeight: 1 }}>⚠</Box>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#c62828', mb: 0.5 }}>
                  Manager Rejection Comments
                </Typography>
                <Typography variant="body2" sx={{ color: '#7f1d1d', whiteSpace: 'pre-wrap', lineHeight: 1.7, fontWeight: 500 }}>
                  {header.rejection_comments}
                </Typography>
              </Box>
            </Box>
          )}
          {/* Company Information Section */}
          {companyDataLoading ? (
            <Box sx={{
              marginBottom: 3,
              padding: 2,
              backgroundColor: '#f5f5f5',
              borderRadius: 1,
              border: '1px solid #e0e0e0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '120px'
            }}>
              <CircularProgress size={30} />
              <Typography variant="body2" sx={{ marginLeft: 2, color: 'text.secondary' }}>
                Loading company information...
              </Typography>
            </Box>
          ) : companyData.company_id && (
            <Box sx={{
              marginBottom: 3,
              padding: 2,
              backgroundColor: '#f5f5f5',
              borderRadius: 1,
              border: '1px solid #e0e0e0'
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                {/* Company Logo */}
                {companyData.logo_url && (
                  <Box sx={{ marginRight: 3 }}>
                    <img
                      src={companyData.logo_url}
                      alt={`${companyData.company_name} Logo`}
                      style={{
                        maxHeight: '80px',
                        maxWidth: '150px',
                        objectFit: 'contain'
                      }}
                    />
                  </Box>
                )}

                {/* Company Address and Contact Info */}
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, marginBottom: 0.5 }}>
                    {companyData.company_name}
                  </Typography>
                  {companyData.address && (
                    <Typography variant="body2" sx={{ marginBottom: 0.5, whiteSpace: 'pre-line' }}>
                      {companyData.address}
                    </Typography>
                  )}
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', marginTop: 1 }}>
                    {companyData.phone && (
                      <Typography variant="body2">
                        <strong>Tel:</strong> {companyData.phone}
                      </Typography>
                    )}
                    {companyData.email && (
                      <Typography variant="body2">
                        <strong>Email:</strong> {companyData.email}
                      </Typography>
                    )}
                    {companyData.website && (
                      <Typography variant="body2">
                        <strong>Website:</strong> {companyData.website}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>
          )}

          {/* Header Section */}
          <Box sx={{ marginBottom: 3 }}>
            <Typography variant="h6" sx={{ marginBottom: 2 }}>PO Details</Typography>

            {/* PO Number Field with Generate Button */}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', marginBottom: 2 }}>
              <TextField
                label="PO Number"
                size="small"
                variant="outlined"
                value={header.po_number || ''}
                onChange={(e) => setHeader({ ...header, po_number: e.target.value.toUpperCase() })}
                placeholder="P00001"
                inputProps={{ maxLength: 6, style: { textTransform: 'uppercase' } }}
                sx={{ flex: 1, minWidth: 120 }}
                disabled={isViewMode}
              />
              {!isViewMode && (
                <>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={autoGeneratePONumber}
                    sx={{ height: 40, minWidth: 100 }}
                  >
                    Generate
                  </Button>
                  <span style={{ fontSize: 11, color: poNumberValidity.ok ? '#4caf50' : '#f44336', marginTop: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {poNumberValidity.ok ? '✓ Valid' : `✕ ${poNumberValidity.msg}`}
                  </span>
                </>
              )}
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              {/* Chargeable/Non-chargeable */}
              <TextField
                select
                label="Type"
                value={header.is_chargeable ? 'chargeable' : 'non-chargeable'}
                onChange={(e) => setHeader({ ...header, is_chargeable: e.target.value === 'chargeable' })}
                fullWidth
                disabled={isViewMode}
              >
                <MenuItem value="non-chargeable">Non-chargeable</MenuItem>
                <MenuItem value="chargeable">Chargeable</MenuItem>
              </TextField>

              {/* Created By */}
              <TextField
                label="Created By"
                value={header.created_by}
                onChange={(e) => setHeader({ ...header, created_by: e.target.value })}
                fullWidth
                disabled={isViewMode}
              />

              {/* PO Type Dropdown */}
              <TextField
                select
                label="PO Type"
                value={header.po_type || ''}
                onChange={(e) => {
                  const newType = e.target.value || null;
                  setHeader({
                    ...header,
                    po_type: newType,
                    contract_id: null,
                    vo_id: null,
                    design_change_number: null
                  });
                  if (newType && projectId) {
                    fetchPoForOptions(newType, projectId);
                  } else {
                    setPoForOptions([]);
                  }
                }}
                fullWidth
                disabled={isViewMode}
              >
                <MenuItem value="">-- Select PO Type --</MenuItem>
                <MenuItem value="Contract">Contract</MenuItem>
                <MenuItem value="VO">VO</MenuItem>
                <MenuItem value="Design Change">Design Change</MenuItem>
              </TextField>

              {/* PO For Dropdown (Conditional) */}
              {header.po_type && (
                <TextField
                  select
                  label={`PO For (${header.po_type})`}
                  disabled={poForLoading || isViewMode}
                  value={
                    header.po_type === 'Contract' ? (header.contract_id || '') :
                    header.po_type === 'VO' ? (header.vo_id || '') :
                    header.po_type === 'Design Change' ? (header.design_change_number || '') : ''
                  }
                  onChange={(e) => {
                    const val = e.target.value;
                    if (header.po_type === 'Contract') {
                      setHeader({ ...header, contract_id: val ? Number(val) : null, vo_id: null, design_change_number: null });
                    } else if (header.po_type === 'VO') {
                      // For VOs, set BOTH vo_id and contract_id to same value
                      // This ensures backend can query by contract_id for remaining balance calculation
                      setHeader({ ...header, vo_id: val ? Number(val) : null, contract_id: val ? Number(val) : null, design_change_number: null });
                    } else if (header.po_type === 'Design Change') {
                      setHeader({ ...header, design_change_number: val || null, contract_id: null, vo_id: null });
                    }
                  }}
                  fullWidth
                >
                  {poForLoading ? (
                    <MenuItem value="">Loading...</MenuItem>
                  ) : poForOptions.length === 0 ? (
                    <MenuItem value="">No data available</MenuItem>
                  ) : (
                    [
                      <MenuItem key="empty" value="">-- Select {header.po_type} --</MenuItem>,
                      ...poForOptions.map((opt: any) => (
                        <MenuItem key={opt.id} value={opt.id}>
                          {opt.label}
                        </MenuItem>
                      ))
                    ]
                  )}
                </TextField>
              )}

              <TextField
                label="Vendor"
                value={header.vendor_name}
                onChange={(e) => setHeader({ ...header, vendor_name: e.target.value })}
                required
                fullWidth
                disabled={isViewMode}
              />
              <TextField
                label="Currency"
                value={header.po_currency}
                onChange={(e) => setHeader({ ...header, po_currency: e.target.value })}
                fullWidth
                disabled={isViewMode}
              />
              <TextField
                label="Date of Issue"
                type="date"
                value={header.date_issued}
                onChange={(e) => setHeader({ ...header, date_issued: e.target.value })}
                InputLabelProps={{ shrink: true }}
                fullWidth
                disabled={isViewMode}
              />

              {/* MR Reference (shown when PO is based on MR) */}
              {header.mr_for && (
                <TextField
                  label="MR Reference"
                  value={header.mr_for}
                  fullWidth
                  disabled
                  InputLabelProps={{ shrink: true }}
                />
              )}

              {/* GST % (only if chargeable) */}
              {header.is_chargeable && (
                <TextField
                  label="GST %"
                  type="number"
                  value={header.gst}
                  onChange={(e) => setHeader({ ...header, gst: e.target.value })}
                  inputProps={{ min: 0, step: 0.01 }}
                  fullWidth
                  disabled={isViewMode}
                />
              )}
            </Box>
          </Box>

          <Divider sx={{ marginY: 3 }} />

          {/* Delivery & People Section */}
          <Box sx={{ marginBottom: 3 }}>
            <Typography variant="h6" sx={{ marginBottom: 2 }}>Delivery & Contact</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField
                label="Delivery Venue"
                value={header.delivery_venue}
                onChange={(e) => setHeader({ ...header, delivery_venue: e.target.value })}
                fullWidth
                disabled={isViewMode}
              />
              <TextField
                label="Delivery Date"
                type="date"
                value={header.delivery_date}
                onChange={(e) => setHeader({ ...header, delivery_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                fullWidth
                disabled={isViewMode}
              />
              <TextField
                label="Recipient"
                value={header.delivery_recipient}
                onChange={(e) => setHeader({ ...header, delivery_recipient: e.target.value })}
                fullWidth
                disabled={isViewMode}
              />
              <TextField
                label="HP / Contact"
                value={header.delivery_hp}
                onChange={(e) => setHeader({ ...header, delivery_hp: e.target.value })}
                fullWidth
                disabled={isViewMode}
              />
              <TextField
                label="Ordered By"
                value={header.ordered_by}
                onChange={(e) => setHeader({ ...header, ordered_by: e.target.value })}
                fullWidth
                disabled={isViewMode}
              />
              <TextField
                label="Approved By"
                value={header.approved_by}
                disabled={true}
                onChange={(e) => setHeader({ ...header, approved_by: e.target.value })}
                helperText="Auto-filled when approved"
                fullWidth
              />
              <TextField
                label="Delivery Terms"
                value={header.delivery_terms}
                onChange={(e) => setHeader({ ...header, delivery_terms: e.target.value })}
                fullWidth
                disabled={isViewMode}
              />
              <TextField
                label="Payment Terms"
                value={header.payment_terms}
                onChange={(e) => setHeader({ ...header, payment_terms: e.target.value })}
                fullWidth
                disabled={isViewMode}
              />
            </Box>
          </Box>

          <Divider sx={{ marginY: 3 }} />

          {/* Contract Terms (only for non-chargeable) */}
          {!header.is_chargeable && (
            <>
              <Box sx={{ marginBottom: 3 }}>
                <Typography variant="h6" sx={{ marginBottom: 2 }}>Contract Terms</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <Box sx={{ gridColumn: 'span 2' }}>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: '#555' }}>Contract Period</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <TextField
                        label="From"
                        type="date"
                        value={header.contract_from}
                        onChange={(e) => setHeader({ ...header, contract_from: e.target.value })}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        disabled={isViewMode}
                      />
                      <TextField
                        label="To"
                        type="date"
                        value={header.contract_to}
                        onChange={(e) => setHeader({ ...header, contract_to: e.target.value })}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        disabled={isViewMode}
                      />
                    </Box>
                  </Box>
                  <TextField
                    label="DLP / Warranty (months)"
                    type="number"
                    value={header.warranty_months}
                    onChange={(e) => setHeader({ ...header, warranty_months: e.target.value })}
                    inputProps={{ min: 0 }}
                    fullWidth
                    disabled={isViewMode}
                  />
                  <TextField
                    select
                    label="Maintenance Services"
                    value={header.maintenance_service}
                    onChange={(e) => setHeader({ ...header, maintenance_service: e.target.value })}
                    fullWidth
                    disabled={isViewMode}
                  >
                    <MenuItem value="NA">NA</MenuItem>
                    <MenuItem value="Yes">Yes</MenuItem>
                    <MenuItem value="No">No</MenuItem>
                  </TextField>
                </Box>
              </Box>
              <Divider sx={{ marginY: 3 }} />
            </>
          )}

          {/* Notes Section */}
          <Box sx={{
            marginBottom: 3,
            ...(isViewMode && !header.notes ? { '@media print': { display: 'none' } } : {})
          }}>
            <Typography variant="h6" sx={{ marginBottom: 2 }}>Notes</Typography>
            <TextField
              value={header.notes}
              onChange={(e) => setHeader({ ...header, notes: e.target.value })}
              multiline
              rows={3}
              fullWidth
              disabled={isViewMode}
              sx={{ '@media print': { display: 'none' } }}
            />
            <Box sx={{ display: 'none', '@media print': { display: 'block', whiteSpace: 'pre-wrap', fontSize: '0.875rem', lineHeight: 1.7 } }}>
              {header.notes}
            </Box>
          </Box>

          <Divider sx={{ marginY: 3, ...(isViewMode && !header.terms_and_conditions ? { '@media print': { display: 'none' } } : {}) }} />

          {/* Terms & Conditions Section */}
          <Box sx={{
            marginBottom: 3,
            ...(isViewMode && !header.terms_and_conditions ? { '@media print': { display: 'none' } } : {})
          }}>
            <Typography variant="h6" sx={{ marginBottom: 2 }}>Terms & Conditions</Typography>
            <TextField
              value={header.terms_and_conditions}
              onChange={(e) => setHeader({ ...header, terms_and_conditions: e.target.value })}
              multiline
              rows={6}
              fullWidth
              disabled={isViewMode}
              sx={{ '@media print': { display: 'none' } }}
            />
            <Box sx={{ display: 'none', '@media print': { display: 'block', whiteSpace: 'pre-wrap', fontSize: '0.875rem', lineHeight: 1.7 } }}>
              {header.terms_and_conditions}
            </Box>
          </Box>

          <Divider sx={{ marginY: 3 }} />

          {/* Items Section */}
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
              <Typography variant="h6">PO Items</Typography>
              {!isViewMode && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleAddItem}
                >
                  Add Item
                </Button>
              )}
            </Box>

            <Box sx={{ width: '100%', '@media print': { '& .MuiDataGrid-footerContainer': { display: 'none' }, '& .MuiDataGrid-toolbarContainer': { display: 'none' } } }}>
              <DataGrid
                rows={itemRows}
                columns={poItemColumns}
                getRowId={(row) => row._id}
                editMode="row"
                rowModesModel={itemRowModesModel}
                onRowModesModelChange={handleItemRowModesModelChange}
                onRowDoubleClick={(params) => {
                  if (!isViewMode && params.row) {
                    setItemRowModesModel(prev => ({ ...prev, [params.id]: { mode: GridRowModes.Edit } }));
                  }
                }}
                onRowEditStop={handleItemRowEditStop}
                processRowUpdate={processItemRowUpdate}
                autoHeight
                disableRowSelectionOnClick
                pageSizeOptions={[10, 25, 50]}
                initialState={{
                  pagination: { paginationModel: { pageSize: 10 } }
                }}
                slots={{ toolbar: GridToolbar }}
                slotProps={{
                  toolbar: {
                    showQuickFilter: true,
                    quickFilterProps: { debounceMs: 300 },
                    printOptions: { disableToolbarButton: true },
                    csvOptions: { disableToolbarButton: true },
                  },
                }}
                rowHeight={48}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  '& .MuiDataGrid-toolbarContainer': {
                    padding: '8px 12px',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    gap: 1,
                  },
                  '& .MuiDataGrid-cell': {
                    paddingX: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    fontSize: '0.875rem'
                  },
                  '& .MuiDataGrid-row': {
                    '&:nth-of-type(even)': {
                      backgroundColor: 'action.hover',
                      opacity: 0.8
                    },
                    '&:hover': {
                      backgroundColor: 'action.hover',
                      cursor: isViewMode ? 'default' : 'pointer'
                    }
                  },
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: 'grey.100',
                    borderBottom: '2px solid',
                    borderColor: 'divider',
                    fontSize: '0.8rem',
                    fontWeight: 700
                  },
                  '& .MuiDataGrid-columnHeaderTitle': {
                    fontWeight: 700,
                    fontSize: '0.8rem'
                  },
                  '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
                    outline: 'none'
                  },
                  '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
                    outline: 'none'
                  }
                }}
              />
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: 2 }}>
              <Typography variant="h6">
                Grand Total: {formatCurrency(calculateGrandTotal(), header.po_currency, companyData.number_format)}
              </Typography>
            </Box>
          </Box>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={saving}>
            {isViewMode ? 'Close' : 'Cancel'}
          </Button>
          {isViewMode && access === 'write' && header.status !== 'Approved' && (
            <Button
              onClick={() => {
                setIsViewMode(false);
                setIsEditMode(true);
              }}
              variant="outlined"
              color="primary"
            >
              Edit
            </Button>
          )}
          {isViewMode && (
            <Button onClick={() => {
              const prev = document.title;
              document.title = `PO-${header.po_number || 'draft'}`;
              window.print();
              document.title = prev;
            }} variant="contained" color="primary">
              Print / PDF
            </Button>
          )}
          {!isViewMode && (
            <Button
              onClick={handleSavePO}
              variant="contained"
              disabled={saving}
            >
              {saving ? (isEditMode ? 'Saving...' : 'Creating...') : (isEditMode ? 'Save PO' : 'Create PO')}
            </Button>
          )}
          {!isViewMode && isEditMode && (header.status === 'Draft' || header.status === 'Rejected') && (
            <Button
              onClick={async () => {
                const poNumber = header.po_number;
                await handleSavePO();
                if (poNumber) await handleSendForApproval(poNumber);
              }}
              variant="contained"
              color="success"
              disabled={saving}
            >
              Send for Approval
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this Purchase Order?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeletePO} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Documents Dialog */}
      <Dialog
        open={documentsDialogOpen}
        onClose={() => setDocumentsDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Box>
            <Typography variant="h6" fontWeight={600}>
              Purchase Order Documents
            </Typography>
            {selectedPOForDocs && (
              <Typography variant="body2" color="text.secondary">
                PO: {selectedPOForDocs.po_number} - {selectedPOForDocs.vendor_name}
              </Typography>
            )}
          </Box>
          <IconButton onClick={() => setDocumentsDialogOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {selectedPOForDocs && (
            <PODocuments
              poNumber={selectedPOForDocs.po_number}
              projectId={projectId}
              canWrite={access === 'write'}
              onDocumentCountChange={() => fetchPOs()}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default QSPurchaseOrders;
